import { useEffect, useMemo, useRef, useState } from "react";
import type React from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { useMobile } from "../../context/MobileContext";
import { clientOverviewPath, clientPortalPath } from "../../nav/consultantNav";
import { clientNeedsAction } from "../../lib/portfolioStatus";
import { theme } from "../../theme";
import { ConsultantClientView } from "../ConsultantClientView";
import { useConsultantShell } from "./ConsultantShellContext";
import { ScopeToggle } from "./ScopeToggle";
import { scoreColor } from "./types";
import type { OrgSummary } from "./types";

// ── /consultant/clients/:orgId ────────────────────────────────────────────
// The client management view: a persistent client list on the left (hidden
// on phones, where the sidebar drawer and the top-bar switcher take over) and
// ConsultantClientView on the right. The list follows the My clients / Whole
// firm choice, filters to clients needing action or set-up, sorts, collapses,
// and walks with the arrow keys. "View as client" opens the read-through,
// except for the consultancy's own organisation, which is a real client session.

type LifecycleBadge = "attention" | "active" | "setup" | "none";

function clientBadge(org: OrgSummary): LifecycleBadge {
  if (clientNeedsAction(org)) return "attention";
  if (org.latestScore !== null) return "active";
  if ((org.frameworks?.length ?? 0) > 0) return "setup";
  return "none";
}

const BADGE_LABELS: Record<LifecycleBadge, string> = {
  attention: "Needs action",
  active: "Active",
  setup: "Setup needed",
  none: "No frameworks",
};

/** One line under the name saying why the client is where it is. */
function clientReason(org: OrgSummary): string {
  const parts: string[] = [];
  if (org.openFindings > 0) parts.push(`${org.openFindings} open finding${org.openFindings === 1 ? "" : "s"}`);
  if (org.pendingTierRequests > 0) parts.push("Tier change requested");
  if (parts.length > 0) return parts.join(" · ");
  if ((org.frameworks?.length ?? 0) === 0) return "No standards enrolled";
  if (org.latestScore === null) return "Not yet assessed";
  const lead = org.assignees?.find((a) => a.role === "lead")?.displayName;
  return lead ? `Lead: ${lead}` : "No lead consultant";
}

// Demo organisations carry "(Demo)" in their name; a tag reads better than a
// truncated suffix. Display only.
const DEMO_SUFFIX = /\s*\(Demo\)\s*$/i;

type ListFilter = "all" | "attention" | "setup";
type ListSort = "attention" | "name" | "score";

const SORT_LABELS: Record<ListSort, string> = {
  attention: "Needs action first",
  name: "Name A-Z",
  score: "Lowest score first",
};

const COLLAPSED_KEY = "consultant.clientList.collapsed";
const SORT_KEY = "consultant.clientList.sort";

function readPref(key: string): string | null {
  try { return window.localStorage.getItem(key); } catch { return null; }
}
function writePref(key: string, value: string) {
  try { window.localStorage.setItem(key, value); } catch { /* per-viewer convenience only */ }
}

function sortClients(orgs: OrgSummary[], sort: ListSort): OrgSummary[] {
  const byName = (a: OrgSummary, b: OrgSummary) => a.name.localeCompare(b.name);
  const score = (o: OrgSummary) => o.latestScore ?? 101;
  return [...orgs].sort((a, b) => {
    if (sort === "name") return byName(a, b);
    if (sort === "score") return score(a) - score(b) || byName(a, b);
    const act = Number(clientNeedsAction(b)) - Number(clientNeedsAction(a));
    return act || (b.openFindings - a.openFindings) || score(a) - score(b) || byName(a, b);
  });
}

function ClientListPanel({ selectedId, onSelect }: { selectedId: string; onSelect: (id: string) => void }) {
  const { portfolio, scope, user } = useConsultantShell();
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<ListFilter>("all");
  const [sort, setSort] = useState<ListSort>(() => {
    const saved = readPref(SORT_KEY);
    return saved === "name" || saved === "score" || saved === "attention" ? saved : "attention";
  });
  const [collapsed, setCollapsed] = useState(() => readPref(COLLAPSED_KEY) === "1");
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const setSortPref = (next: ListSort) => { setSort(next); writePref(SORT_KEY, next); };
  const setCollapsedPref = (next: boolean) => { setCollapsed(next); writePref(COLLAPSED_KEY, next ? "1" : "0"); };

  // The same "My clients" rule as the Clients table: assigned to me, or to nobody yet.
  const inScope = useMemo(() => portfolio.orgs.filter((o) => {
    if (scope !== "mine") return true;
    const a = o.assignees ?? [];
    return a.length === 0 || a.some((x) => x.userId === user.id);
  }), [portfolio.orgs, scope, user.id]);

  const q = search.trim().toLowerCase();
  const matching = inScope.filter((o) => !q
    || o.name.toLowerCase().includes(q)
    || (o.sector ?? "").toLowerCase().includes(q)
    || (o.assignees ?? []).some((a) => (a.displayName ?? "").toLowerCase().includes(q)));
  const isSetup = (o: OrgSummary) => { const b = clientBadge(o); return b === "setup" || b === "none"; };
  const counts: Record<ListFilter, number> = {
    all: matching.length,
    attention: matching.filter((o) => clientBadge(o) === "attention").length,
    setup: matching.filter(isSetup).length,
  };
  const filtered = sortClients(matching.filter((o) =>
    filter === "attention" ? clientBadge(o) === "attention" : filter === "setup" ? isSetup(o) : true), sort);

  // Keep the open client in view when arriving from elsewhere.
  useEffect(() => {
    if (collapsed) return;
    const el = listRef.current?.querySelector<HTMLElement>(`[data-org-id="${CSS.escape(selectedId)}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [selectedId, collapsed, portfolio.orgs.length]);

  const rowEls = () => Array.from(listRef.current?.querySelectorAll<HTMLElement>("[data-org-id]") ?? []);
  const focusRow = (index: number) => {
    const rows = rowEls();
    if (rows.length > 0) rows[Math.max(0, Math.min(rows.length - 1, index))].focus();
  };

  // Arrow keys walk the list; Enter / Space open the focused client (native button).
  const onListKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const rows = rowEls();
    const at = rows.indexOf(document.activeElement as HTMLElement);
    if (at < 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); focusRow(at + 1); }
    else if (e.key === "ArrowUp") { e.preventDefault(); if (at === 0) searchRef.current?.focus(); else focusRow(at - 1); }
    else if (e.key === "Home") { e.preventDefault(); focusRow(0); }
    else if (e.key === "End") { e.preventDefault(); focusRow(rows.length - 1); }
  };

  const onSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") { e.preventDefault(); focusRow(0); }
    else if (e.key === "Enter" && filtered.length > 0) { e.preventDefault(); onSelect(filtered[0].id); }
    else if (e.key === "Escape" && search) { e.preventDefault(); setSearch(""); }
  };

  const iconBtn: React.CSSProperties = {
    background: "transparent", border: `1px solid ${theme.color.border}`, borderRadius: 3,
    color: theme.color.subtle, cursor: "pointer", fontFamily: theme.font, fontSize: 12,
    padding: "4px 7px", lineHeight: 1, flexShrink: 0,
  };

  if (collapsed) {
    return (
      <div style={{
        width: 40, flexShrink: 0, borderRight: `1px solid ${theme.color.border}`, background: theme.color.panel,
        display: "flex", flexDirection: "column", alignItems: "center", paddingTop: 12,
      }}>
        <button type="button" onClick={() => setCollapsedPref(false)} style={iconBtn}
          aria-label="Show client list" title="Show client list">
          &raquo;
        </button>
      </div>
    );
  }

  const chip = (key: ListFilter, label: string) => {
    const active = filter === key;
    return (
      <button key={key} type="button" aria-pressed={active} onClick={() => setFilter(key)} style={{
        padding: "3px 8px", borderRadius: 3, fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: theme.font,
        border: `1px solid ${active ? theme.color.accent : theme.color.border}`,
        background: active ? theme.color.accentBg : "transparent",
        color: active ? theme.color.accentText : theme.color.subtle, whiteSpace: "nowrap",
      }}>
        {label} {counts[key]}
      </button>
    );
  };

  const field: React.CSSProperties = {
    background: theme.color.bg, border: `1px solid ${theme.color.border}`, borderRadius: 3,
    fontSize: 12, color: theme.color.text, fontFamily: theme.font, width: "100%", boxSizing: "border-box",
  };

  return (
    <div style={{
      width: 280, flexShrink: 0, borderRight: `1px solid ${theme.color.border}`,
      display: "flex", flexDirection: "column", overflow: "hidden", background: theme.color.panel,
    }}>
      <div style={{ padding: "12px 12px 10px", display: "flex", flexDirection: "column", gap: 8, borderBottom: `1px solid ${theme.color.border}` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <ScopeToggle compact />
          <span style={{ flex: 1 }} />
          <button type="button" onClick={() => setCollapsedPref(true)} style={iconBtn}
            aria-label="Hide client list" title="Hide client list">
            &laquo;
          </button>
        </div>
        <input
          ref={searchRef}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={onSearchKeyDown}
          placeholder="Search name, sector or consultant"
          aria-label="Search clients"
          style={{ ...field, padding: "6px 10px", outline: "none" }}
        />
        <div style={{ display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
          <div role="group" aria-label="Filter clients" style={{ display: "flex", gap: 4 }}>
            {chip("all", "All")}
            {chip("attention", "Action")}
            {chip("setup", "Setup")}
          </div>
        </div>
        <select
          value={sort}
          onChange={(e) => setSortPref(e.target.value as ListSort)}
          aria-label="Sort clients"
          style={{ ...field, padding: "4px 6px", fontSize: 11, color: theme.color.subtle }}
        >
          {(Object.keys(SORT_LABELS) as ListSort[]).map((k) => <option key={k} value={k}>{SORT_LABELS[k]}</option>)}
        </select>
      </div>

      <div ref={listRef} onKeyDown={onListKeyDown} style={{ flex: 1, overflowY: "auto" }}>
        {portfolio.loading && portfolio.orgs.length === 0 && (
          <div style={{ padding: "16px 12px", fontSize: 12, color: theme.color.faint }}>Loading clients</div>
        )}
        {!portfolio.loading && filtered.length === 0 && (
          <div style={{ padding: "16px 12px", fontSize: 12, color: theme.color.faint }}>
            {portfolio.orgs.length === 0 ? "No clients yet."
              : inScope.length === 0 ? "No clients assigned to you. Switch to Whole firm to see everyone."
              : "No clients match."}
          </div>
        )}
        {filtered.map((org) => {
          const badge = clientBadge(org);
          const isSelected = org.id === selectedId;
          const isDemo = DEMO_SUFFIX.test(org.name);
          const name = org.name.replace(DEMO_SUFFIX, "");
          const reason = clientReason(org);
          return (
            <button
              key={org.id}
              data-org-id={org.id}
              type="button"
              onClick={() => onSelect(org.id)}
              aria-current={isSelected ? "page" : undefined}
              aria-label={`${org.name}, ${BADGE_LABELS[badge]}${org.latestScore !== null ? `, ${org.latestScore}%` : ""}, ${reason}`}
              title={org.name}
              style={{
                display: "block", width: "100%", textAlign: "left",
                padding: "8px 12px", background: isSelected ? theme.color.accentBg : "transparent",
                border: "none", borderBottom: `1px solid ${theme.color.border}`,
                borderLeft: isSelected ? `3px solid ${theme.color.accent}` : "3px solid transparent",
                cursor: "pointer", transition: "background 0.1s", fontFamily: theme.font,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span aria-hidden title={BADGE_LABELS[badge]} style={{
                  width: 7, height: 7, borderRadius: "50%", flexShrink: 0,
                  background: badge === "attention" ? theme.color.warningText
                    : badge === "active" ? theme.color.successText : theme.color.faint,
                }} />
                <span style={{ flex: 1, minWidth: 0, fontSize: 12, fontWeight: 600, color: isSelected ? theme.color.accentText : theme.color.heading, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {name}
                </span>
                {isDemo && (
                  <span style={{ fontSize: 9, fontWeight: 700, padding: "1px 4px", borderRadius: 3, background: theme.color.bg, color: theme.color.faint, border: `1px solid ${theme.color.border}`, flexShrink: 0 }}>
                    Demo
                  </span>
                )}
                {org.latestScore !== null && (
                  <span style={{ fontSize: 11, fontWeight: 700, color: scoreColor(org.latestScore), flexShrink: 0 }}>
                    {org.latestScore}%
                  </span>
                )}
              </div>
              <div style={{ marginTop: 2, paddingLeft: 13, fontSize: 11, color: badge === "attention" ? theme.color.warningText : theme.color.faint, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {reason}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function ConsultantClientRoute() {
  const { orgId = "" } = useParams<{ orgId: string }>();
  const navigate = useNavigate();
  const isMobile = useMobile();
  const { clients, selfOrg, recordRecentClient, enterOwnOrgPortal } = useConsultantShell();
  const listed = clients.some((c) => c.id === orgId);
  const isOwnOrg = !!selfOrg && selfOrg.id === orgId;

  // Only the firm's own clients are remembered (a platform owner reading
  // through another firm's organisation leaves no trace).
  useEffect(() => {
    if (orgId && listed) recordRecentClient(orgId);
  }, [orgId, listed, recordRecentClient]);

  if (!orgId) return <Navigate to="/consultant/clients" replace />;

  // "Client portal" for the consultancy's own organisation always means the
  // real thing; for any other client it is the consultant's read-through.
  const openPortal = () => {
    if (isOwnOrg) void enterOwnOrgPortal();
    else navigate(clientPortalPath(orgId));
  };

  return (
    <div style={{ display: "flex", height: "100%", minHeight: 0 }}>
      {!isMobile && <ClientListPanel selectedId={orgId} onSelect={(id) => navigate(clientOverviewPath(id))} />}
      <div style={{ flex: 1, minWidth: 0, overflow: "hidden", display: "flex", flexDirection: "column" }}>
        <ConsultantClientView
          key={orgId}
          orgId={orgId}
          onBack={() => navigate("/consultant/clients")}
          onViewAsClient={openPortal}
        />
      </div>
    </div>
  );
}
